import { useState, useMemo, useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';

import { RootState } from '../../store';
import {
  useGetStaffQuery,
  useGetPanelQuery,
  useGetSubjectQuery,
  useLazyGetDesgVacNeedQuery,
  useLazyGetCheckVacancyQuery,
  useAddVacancyNeedMutation,
  useAddVacancyNeedLogMutation,
} from './counselling.services';
import { alertService } from '../../shared/services/alertService';
import {
  SUBJECT_DESIGNATIONS,
  getSubjectListByDesignation,
} from '../../shared/services/commonService';

/**
 * Migrated from Angular `ChangeVacancyComponent`
 * (src/app/views/pages/counselling/change-vacancy).
 *
 * Route: /counselling/change-vacancy
 *
 * Search a school's vacancy/need by Designation + UDISE, view results in a
 * table with inline edit of Vacancy/Need, and add a new vacancy/need record
 * through a dialog.
 */

// ── Dropdown option shape ────────────────────────────────────────────────────

interface Option {
  label: string;
  value: string;
}

// ── Add-dialog school details shape ──────────────────────────────────────────

interface UdiseDetails {
  udise: string;
  Vacancy: number;
  Need: number;
  DesignationName: string;
  Block: string;
  school: string;
  schoolId: string;
  Category: string;
  Management: string;
  District: string;
}

// ── Component ────────────────────────────────────────────────────────────────

const ChangeVacancy = () => {
  const s = useSelector((state: RootState) => state.session);
  const userId = s.username ?? '';

  // ── Search form ──────────────────────────────────────────────────────────
  const [designation, setDesignation] = useState<string>('');
  const [udise, setUdise] = useState<string>('');
  const [isShowTable, setIsShowTable] = useState(false);
  const [globalFilter, setGlobalFilter] = useState('');

  // Rows for the results table (transformed from the API — need local edit flags)
  const [desVacancyList, setDesVacancyList] = useState<any[]>([]);

  // ── Add dialog ─────────────────────────────────────────────────────────────
  const [newAddModel, setNewAddModel] = useState(false);
  const [udiseDetails, setUdiseDetails] = useState<UdiseDetails | null>(null);
  const [panel, setPanel] = useState<string>('');
  const [subject, setSubject] = useState<string>('');
  const [subjectList, setSubjectList] = useState<Option[]>([]);

  // ── Static dropdown data (fetched once on mount) ───────────────────────────
  const { data: staffRaw } = useGetStaffQuery();
  const { data: panelRaw } = useGetPanelQuery();
  const { data: subjectRaw } = useGetSubjectQuery();

  // ── Lazy queries (fire on button click, like Angular subscribe) ────────────
  const [triggerDesgVacNeed, { isFetching: loading }] =
    useLazyGetDesgVacNeedQuery();
  const [triggerCheckVacancy] = useLazyGetCheckVacancyQuery();

  const [addVacancyNeed] = useAddVacancyNeedMutation();
  const [addVacancyNeedLog] = useAddVacancyNeedLogMutation();

  // ── Dropdown option lists ──────────────────────────────────────────────────

  const DesignationList: Option[] = useMemo(() => {
    if (!staffRaw) return [];
    const list = staffRaw.AllStaffType ?? [];
    if (!Array.isArray(list)) return [];
    return list.map((d: any) => ({ label: d.label, value: String(d.value) }));
  }, [staffRaw]);

  const PanelList: Option[] = useMemo(() => {
    if (!panelRaw) return [];
    const list = panelRaw.Panel ?? [];
    if (!Array.isArray(list)) return [];
    return list.map((p: any) => ({ label: p.label, value: String(p.id) }));
  }, [panelRaw]);

  // ── Submit / fetch vacancy-need list ───────────────────────────────────────

  const submit = useCallback(async () => {
    if (!designation || !udise) {
      alertService.error('Please fill all fields');
      return;
    }
    setIsShowTable(true);
    try {
      const data: any = await triggerDesgVacNeed({
        designation,
        udiseCode: udise,
      }).unwrap();

      if (data.dataStatus) {
        const rows = (data.result ?? []).map((row: any) => {
          const match = PanelList.find((pt) => pt.value === String(row.panel));
          return {
            ...row,
            panelLabel: match ? match.label : '',
            IsEdit: false,
          };
        });
        setDesVacancyList(rows);
      } else {
        setDesVacancyList([]);
        alertService.error(data.message);
      }
    } catch {
      setDesVacancyList([]);
      alertService.error('Unable to fetch vacancy details');
    }
  }, [designation, udise, triggerDesgVacNeed, PanelList]);

  // ── Inline edit handlers ───────────────────────────────────────────────────

  const handleEdit = useCallback((rowIndex: number) => {
    setDesVacancyList((prev) =>
      prev.map((r, i) => (i === rowIndex ? { ...r, IsEdit: true } : r))
    );
  }, []);

  const handleEditCancel = useCallback(
    (rowIndex: number) => {
      setDesVacancyList((prev) =>
        prev.map((r, i) => (i === rowIndex ? { ...r, IsEdit: false } : r))
      );
      submit();
    },
    [submit]
  );

  /** Update a single editable cell (vac / need) in a row. */
  const handleCellChange = useCallback(
    (rowIndex: number, field: 'vac' | 'need', value: string) => {
      const clean = value.replace(/\D/g, '');
      setDesVacancyList((prev) =>
        prev.map((r, i) => (i === rowIndex ? { ...r, [field]: clean } : r))
      );
    },
    []
  );

  const vacancyLog = useCallback(
    async (log: any) => {
      try {
        const res: any = await addVacancyNeedLog(log).unwrap();
        if (res.dataStatus) {
          setNewAddModel(false);
          // alertService.success(res.message);
          submit();
        } else {
          alertService.error(res.message);
        }
      } catch {
        alertService.error('Unable to save log');
      }
    },
    [addVacancyNeedLog, submit]
  );

  const handleEditSubmit = useCallback(
    async (row: any) => {
      if (!row.need || !row.vac) {
        alertService.error('Please fill vacancy and need');
        return;
      }
      if (!(Number(row.need) >= 0 && Number(row.vac) >= 0)) {
        alertService.error('Vacancy and Need must be greater than zero');
        return;
      }

      const req = {
        records: [
          {
            IndexId: row.id ? row.id : '',
            SchlKeyId: row.school_key_id,
            Panel: row.panel,
            Desg: designation,
            Sub: row.subject,
            Vac: row.vac,
            Need: row.need,
          },
        ],
      };

      try {
        const res: any = await addVacancyNeed(req).unwrap();
        if (res.dataStatus) {
          alertService.success(res.message);
          const log = {
            records: [
              {
                IndexId: null,
                SchlKeyId: row.school_key_id,
                Panel: row.panel,
                Desg: designation,
                Sub: row.subject,
                Vac: row.vac,
                Need: row.need,
                User: userId,
              },
            ],
          };
          vacancyLog(log);
        } else {
          alertService.error(res.message);
        }
      } catch {
        alertService.error('Unable to update vacancy');
      }
    },
    [designation, userId, addVacancyNeed, vacancyLog]
  );

  // ── Add dialog: open + populate school details ─────────────────────────────

  const newAdd = useCallback(async () => {
    setPanel('');
    setSubject('');

    try {
      const res: any = await triggerCheckVacancy({ udiseCode: udise }).unwrap();
      if (!res.dataStatus) {
        alertService.error(res.message);
        return;
      }

      const result =
        Array.isArray(res.result) && res.result.length > 0 ? res.result[0] : {};
      const des = DesignationList.find((d) => d.value === String(designation));

      // Subject list is only relevant for the special designations.
      // Subjects are loaded once on mount (subjectRaw), so just derive here.
      if (SUBJECT_DESIGNATIONS.includes(String(designation))) {
        setSubjectList(getSubjectListByDesignation(subjectRaw, designation));
      } else {
        setSubjectList([]);
      }

      setUdiseDetails({
        udise,
        Vacancy: 0,
        Need: 0,
        DesignationName: des ? des.label : '',
        Block: result.BlkNme ?? '',
        school: result.SchlNme ?? '',
        schoolId: result.SchlId ?? '',
        Category: result.CattyNme ?? '',
        Management: result.Mng ?? '',
        District: result.DisNme ?? '',
      });
      setNewAddModel(true);
    } catch {
      alertService.error('Unable to fetch school details');
    }
  }, [udise, designation, DesignationList, triggerCheckVacancy, subjectRaw]);

  // ── Add dialog: stepper handlers ───────────────────────────────────────────

  const stepVacancy = useCallback((delta: number) => {
    setUdiseDetails((prev) =>
      prev ? { ...prev, Vacancy: Number(prev.Vacancy) + delta } : prev
    );
  }, []);

  const stepNeed = useCallback((delta: number) => {
    setUdiseDetails((prev) =>
      prev ? { ...prev, Need: Number(prev.Need) + delta } : prev
    );
  }, []);

  const handleCancel = useCallback(() => {
    setNewAddModel(false);
  }, []);

  const handleAddSubmit = useCallback(async () => {
    if (!udiseDetails) return;

    if (!panel) {
      alertService.error('Please select panel');
      return;
    }

    const needsSubject = SUBJECT_DESIGNATIONS.includes(String(designation));
    if (needsSubject && !subject) {
      alertService.error('Please select subject');
      return;
    }

    const need = Number(udiseDetails.Need);
    const vac = Number(udiseDetails.Vacancy);
    if (!(need >= 0 && vac >= 0 && (need > 0 || vac > 0))) {
      alertService.error('Vacancy and Need must be greater than zero');
      return;
    }

    const req = {
      records: [
        {
          IndexId: '',
          SchlKeyId: udiseDetails.schoolId,
          Panel: panel,
          Desg: designation,
          Sub: subject,
          Vac: vac,
          Need: need,
        },
      ],
    };

    try {
      const res: any = await addVacancyNeed(req).unwrap();
      if (res.dataStatus) {
        const log = {
          records: [
            {
              IndexId: null,
              SchlKeyId: udiseDetails.schoolId,
              Panel: panel,
              Desg: designation,
              Sub: subject,
              Vac: vac,
              Need: need,
              User: userId,
            },
          ],
        };
        setNewAddModel(false);
        alertService.success(res.message);
        vacancyLog(log);
      } else {
        alertService.error(res.message);
      }
    } catch {
      alertService.error('Unable to add vacancy');
    }
  }, [
    udiseDetails,
    panel,
    subject,
    designation,
    userId,
    addVacancyNeed,
    vacancyLog,
  ]);

  // ── Reset filter when the table hides ──────────────────────────────────────
  useEffect(() => {
    if (!isShowTable) setGlobalFilter('');
  }, [isShowTable]);

  const showSubjectRow = SUBJECT_DESIGNATIONS.includes(String(designation));

  // ── Column templates ───────────────────────────────────────────────────────

  const vacBody = (row: any, rowIndex: number) =>
    row.IsEdit ? (
      <InputText
        value={String(row.vac ?? '')}
        keyfilter="int"
        className="form-control"
        onChange={(e) => handleCellChange(rowIndex, 'vac', e.target.value)}
      />
    ) : (
      <span>{row.vac}</span>
    );

  const needBody = (row: any, rowIndex: number) =>
    row.IsEdit ? (
      <InputText
        value={String(row.need ?? '')}
        keyfilter="int"
        className="form-control"
        onChange={(e) => handleCellChange(rowIndex, 'need', e.target.value)}
      />
    ) : (
      <span>{row.need}</span>
    );

  const actionBody = (row: any, rowIndex: number) =>
    row.IsEdit ? (
      <span>
        <Button
          type="button"
          className="p-button-success mt-2"
          icon="pi pi-check"
          onClick={() => handleEditSubmit(row)}
        />
        &nbsp;
        <Button
          type="button"
          className="p-button-danger mt-2"
          icon="pi pi-times"
          onClick={() => handleEditCancel(rowIndex)}
        />
      </span>
    ) : (
      <Button
        type="button"
        className="p-button-primary mt-2"
        icon="pi pi-user-edit"
        onClick={() => handleEdit(rowIndex)}
      />
    );

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="main-body-content">
      {/* ── Search bar ──────────────────────────────────────────────── */}
      <div className="card p-4">
        <div className="form-group row">
          <div className="col-lg-4">
            <b>Designation</b>
            <Dropdown
              value={designation}
              options={DesignationList}
              optionLabel="label"
              optionValue="value"
              placeholder="Select designation.."
              filter
              filterBy="label"
              resetFilterOnHide
              appendTo="self"
              className="w-100"
              onChange={(e) => setDesignation(e.value)}
            />
          </div>
          <div className="col-lg-4">
            <b>UDISE CODE</b>
            <InputText
              value={udise}
              onChange={(e) => setUdise(e.target.value.replace(/\D/g, ''))}
              onKeyDown={(e) => {
                if (e.key === 'Enter') submit();
              }}
              placeholder="Udise code"
              className="w-100"
              maxLength={11}
              keyfilter="int"
            />
          </div>
          <div className="col-lg-4 mt-4">
            <Button
              type="button"
              icon="fa fa-success"
              className="p-button-success"
              label="Submit"
              onClick={submit}
            />
          </div>
        </div>
      </div>
      <br />

      {/* ── Results table ───────────────────────────────────────────── */}
      {isShowTable && (
        <div className="Counselling-Secondary">
          <DataTable
            value={desVacancyList}
            loading={loading}
            sortMode="single"
            size="small"
            stripedRows
            rowHover
            responsiveLayout="scroll"
            emptyMessage="No Records Found"
            globalFilter={globalFilter}
            globalFilterFields={[
              'ShoolName',
              'Subject',
              'panelLabel',
              'vac',
              'need',
            ]}
            paginator
            rows={10}
            rowsPerPageOptions={[10, 25, 50]}
            currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
            paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
            header={
              <div className="row">
                <div className="col-md-12 col-12 text-right text-end">
                  <span className="p-input-icon-left">
                    <InputText
                      type="text"
                      value={globalFilter}
                      placeholder="Search.."
                      onChange={(e) => setGlobalFilter(e.target.value)}
                    />
                  </span>
                  &nbsp;&nbsp;&nbsp;
                  <Button
                    type="button"
                    icon="fa fa-primary"
                    className="p-button-success"
                    label="Add"
                    onClick={newAdd}
                  />
                </div>
              </div>
            }
          >
            <Column
              header="S.No"
              body={(_r, { rowIndex }) => rowIndex + 1}
              style={{ width: '60px' }}
            />
            <Column field="ShoolName" header="School Name" sortable />
            <Column
              field="Subject"
              header="Subject"
              sortable
              body={(row) => (row.Subject ? row.Subject : '-')}
            />
            <Column field="panelLabel" header="Panel" sortable />
            <Column
              field="vac"
              header="Vacancy"
              sortable
              body={(row, { rowIndex }) => vacBody(row, rowIndex)}
            />
            <Column
              field="need"
              header="Need"
              sortable
              body={(row, { rowIndex }) => needBody(row, rowIndex)}
            />
            <Column
              header="Action"
              body={(row, { rowIndex }) => actionBody(row, rowIndex)}
            />
          </DataTable>
        </div>
      )}

      {/* ── Add dialog ──────────────────────────────────────────────── */}
      <Dialog
        header="Add"
        modal
        visible={newAddModel}
        draggable={false}
        onHide={handleCancel}
        appendTo="self"
        headerClassName="dialog-header-secondary"
        blockScroll
        resizable={false}
        style={{ width: '70vw' }}
      >
        {udiseDetails && (
          <>
            <div className="row">
              <div className="col mt-4">
                <div className="p-datatable p-component p-datatable-gridlines">
                  <div
                    className="p-datatable-header"
                    style={{ padding: '0.5rem 1rem' }}
                  >
                    School Details
                  </div>
                  <div className="p-datatable-wrapper">
                    <table
                      className="p-datatable-table w-100"
                      role="table"
                      style={{
                        borderCollapse: 'collapse',
                        border: '1px solid #dee2e6',
                      }}
                    >
                      <tbody className="p-datatable-tbody">
                        <tr>
                          <td>
                            <b>District</b>
                          </td>
                          <td>{udiseDetails.District}</td>
                        </tr>
                        <tr>
                          <td>
                            <b>Block</b>
                          </td>
                          <td>{udiseDetails.Block}</td>
                        </tr>
                        <tr>
                          <td>
                            <b>UDISE Code</b>
                          </td>
                          <td>{udiseDetails.udise}</td>
                        </tr>
                        <tr>
                          <td>
                            <b>School Name</b>
                          </td>
                          <td>{udiseDetails.school}</td>
                        </tr>
                        <tr>
                          <td>
                            <b>Management</b>
                          </td>
                          <td>{udiseDetails.Management}</td>
                        </tr>
                        <tr>
                          <td>
                            <b>Category</b>
                          </td>
                          <td>{udiseDetails.Category}</td>
                        </tr>
                        <tr>
                          <td>
                            <b>Designation</b>
                          </td>
                          <td>{udiseDetails.DesignationName}</td>
                        </tr>

                        {showSubjectRow && (
                          <tr>
                            <td>
                              <b>Subject</b>
                            </td>
                            <td>
                              <div className="row">
                                <div className="col-5">
                                  <Dropdown
                                    value={subject}
                                    options={subjectList}
                                    optionLabel="label"
                                    optionValue="value"
                                    placeholder="Select subject.."
                                    filter
                                    filterBy="label"
                                    appendTo="self"
                                    className="w-100"
                                    onChange={(e) => setSubject(e.value)}
                                  />
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}

                        <tr>
                          <td>
                            <b>Panel</b>
                          </td>
                          <td>
                            <div className="row">
                              <div className="col-5">
                                <Dropdown
                                  value={panel}
                                  options={PanelList}
                                  optionLabel="label"
                                  optionValue="value"
                                  placeholder="Select panel.."
                                  filter
                                  filterBy="label"
                                  appendTo="self"
                                  className="w-100"
                                  onChange={(e) => setPanel(e.value)}
                                />
                              </div>
                            </div>
                          </td>
                        </tr>

                        <tr>
                          <td>
                            <b>Vacancy</b>
                          </td>
                          <td>
                            <div className="row align-items-center">
                              <div className="col-1 text-right text-end">
                                <Button
                                  type="button"
                                  className="p-button-danger"
                                  icon="pi pi-minus"
                                  onClick={() => stepVacancy(-1)}
                                />
                              </div>
                              <div className="col-3">
                                <InputText
                                  value={String(udiseDetails.Vacancy)}
                                  placeholder="Vacancy Count.."
                                  keyfilter="int"
                                  className="form-control"
                                  onChange={(e) =>
                                    setUdiseDetails((prev) =>
                                      prev
                                        ? {
                                            ...prev,
                                            Vacancy: Number(
                                              e.target.value.replace(
                                                /\D/g,
                                                ''
                                              ) || 0
                                            ),
                                          }
                                        : prev
                                    )
                                  }
                                />
                              </div>
                              <div className="col-1">
                                <Button
                                  type="button"
                                  className="p-button-success"
                                  icon="pi pi-plus"
                                  onClick={() => stepVacancy(1)}
                                />
                              </div>
                            </div>
                          </td>
                        </tr>

                        <tr>
                          <td>
                            <b>Need</b>
                          </td>
                          <td>
                            <div className="row align-items-center">
                              <div className="col-1 text-right text-end">
                                <Button
                                  type="button"
                                  className="p-button-danger"
                                  icon="pi pi-minus"
                                  onClick={() => stepNeed(-1)}
                                />
                              </div>
                              <div className="col-3">
                                <InputText
                                  value={String(udiseDetails.Need)}
                                  placeholder="Need Count.."
                                  keyfilter="int"
                                  className="form-control"
                                  onChange={(e) =>
                                    setUdiseDetails((prev) =>
                                      prev
                                        ? {
                                            ...prev,
                                            Need: Number(
                                              e.target.value.replace(
                                                /\D/g,
                                                ''
                                              ) || 0
                                            ),
                                          }
                                        : prev
                                    )
                                  }
                                />
                              </div>
                              <div className="col-1">
                                <Button
                                  type="button"
                                  className="p-button-success"
                                  icon="pi pi-plus"
                                  onClick={() => stepNeed(1)}
                                />
                              </div>
                            </div>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
            <br />
            <div className="row">
              <div className="col-12 text-right text-end">
                <Button
                  type="button"
                  className="p-button-success"
                  label="Add"
                  onClick={handleAddSubmit}
                />
                &nbsp;
                <Button
                  type="button"
                  className="p-button-danger"
                  label="Cancel"
                  onClick={handleCancel}
                />
              </div>
            </div>
          </>
        )}
      </Dialog>
    </div>
  );
};

export default ChangeVacancy;
